import Ionicons from "@expo/vector-icons/Ionicons";
import { useLocalSearchParams, useRouter } from "expo-router";
import { type ComponentProps, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AppState,
  FlatList,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
} from "react-native";

import { Modal } from "@/components/ui/app-modal";
import { SafeAreaView } from "react-native-safe-area-context";
import { useTranslation } from "react-i18next";

import { ApiError, cuesApi, groupApi } from "@/api";
import { eventsBus } from "@/api/events-socket";
import { USE_MOCK } from "@/api/config";
import {
  CATCH_UP_MAX_PAGES,
  CATCH_UP_PAGE,
  lastServerMessageId,
  mergeMessages,
} from "@/features/realtime/message-merge";
import { ReportModal } from "@/components/report-modal";
import { ThemedText } from "@/components/themed-text";
import { Avatar } from "@/components/ui/avatar";
import { BlurredBackdrop } from "@/components/ui/blurred-backdrop";
import { CompactSwitch } from "@/components/ui/compact-switch";
import { KeyboardAvoidingContainer } from "@/components/ui/keyboard-view";
import { Palette, Radius, Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import { useThreadFollow } from "@/hooks/use-thread-follow";
import { useKeyboardProbe } from "@/hooks/use-keyboard-probe";
import { formatMessageTime } from "@/lib/message-time";
import { shareText } from "@/lib/share-text";
import { systemMessageText } from "@/lib/system-message";
import { canLoadOlder, moreBehind } from "@/lib/load-older";
import {
  cachedGroupHeader,
  cachedGroupMessages,
  minutesLeftUntil,
  rememberGroupHeader,
  rememberGroupMessages,
} from "@/lib/group-cache";
import { entriesForThread, visiblePending } from "@/lib/outbox-core";
import { onOutboxSent, useOutboxStore } from "@/state/outbox-store";
import { probe, probeSpan } from "@/lib/timing-probe";
import { createLatch } from "@/lib/single-tap";
import { socialStrings as strings } from "@/i18n/features/social.i18n";
import { NearbyRadiusSlider } from "@/components/nearby-radius-slider";
import { publicConfig } from "@/config/public";
import { confirm, notify } from "@/lib/dialog";
import { SMALL_GROUP_RADIUS_MAX_M, formatRadiusM } from "@/lib/nearby-radius";
import { useAuthStore } from "@/state/auth-store";
import { requestReflection } from "@/state/reflection-store";
import { useSessionStore } from "@/state/session-store";
import type { SmallGroup, SmallGroupMember, SmallGroupMessage } from "@/types";
import { handleFor } from "@/lib/profile-handle";
import {
  memberActionsFor,
  type MemberActionKind,
} from "@/lib/group-member-actions";

/**
 * Enum values, not copy.
 *
 * These used to be the English sentences themselves — "Near the entrance" and
 * friends — doing three jobs at once: the picker's identity, the branch that
 * chose a draft, and (for `custom`) a value on the wire. Translating them
 * would have broken the branch silently, which is why the drafts below stayed
 * English long after the picker labels were localized.
 *
 * `draftKey` is what each one actually contributes: the message put into the
 * composer for the user to send. It is THEIR words, not the app's, so §3 of
 * the translation brief treats it as modelled speech.
 */
const MEETUP_SPOTS = [
  {
    value: "near_entrance",
    labelKey: strings.nearEntrance,
    draftKey: strings.meetupDraftNearEntrance,
  },
  {
    value: "easy_to_find",
    labelKey: strings.meetupEasyToFind,
    draftKey: strings.meetupDraftEasyToFind,
  },
  {
    value: "quieter",
    labelKey: strings.meetupQuieter,
    draftKey: strings.meetupDraftQuieter,
  },
  {
    value: "custom",
    labelKey: strings.meetupCustom,
    draftKey: strings.meetupDraftCustom,
  },
] as const;

type MeetupSpotValue = (typeof MEETUP_SPOTS)[number]["value"];
const TYPING_REFRESH_MS = 1400;
const TYPING_STOP_MS = 2200;
const PEER_TYPING_TTL_MS = 3500;
const CHAT_MAX_WIDTH = 760;
const GROUP_MESSAGE_MAX_LENGTH = 2000;

/**
 * How often the group's own state is re-read — members, meetup, ready, expiry.
 *
 * Only the state: messages arrive over the socket and gaps are closed with a
 * cursor. This payload is small and bounded by the size of the group, which is
 * what makes a poll affordable here when a full message re-read was not.
 */
const GROUP_STATE_POLL_MS = 10_000;
type GroupActionIcon = ComponentProps<typeof Ionicons>["name"];

type TypingParticipant = {
  userId: string;
  displayName: string;
};

const MEMBER_ACTION_ICON: Record<
  MemberActionKind,
  ComponentProps<typeof Ionicons>["name"]
> = {
  message: "chatbubble-outline",
  cue: "hand-left-outline",
  report: "flag-outline",
  remove: "person-remove-outline",
};

export default function GroupChatScreen() {
  const { id, fromTab } = useLocalSearchParams<{
    id: string;
    fromTab?: string;
  }>();
  const router = useRouter();
  const c = useTheme();
  const { t } = useTranslation();
  const currentUserId = useAuthStore((state) => state.user?.id);
  const setActiveGroup = useSessionStore((state) => state.setActiveGroup);
  const markGroupRead = useSessionStore((state) => state.markGroupRead);
  const receiveGroupMessage = useSessionStore((state) => state.receiveGroupMessage);

  const [group, setGroup] = useState<SmallGroup | null>(null);
  /**
   * What to put in the header until the real group arrives, from whoever last
   * saw this group — the inbox row, the home list, a create or a join.
   *
   * Held APART from `group` on purpose. Everything this screen gates on reads
   * `group`, so seeding that with a remembered object would arm the composer,
   * the leave option and the membership branches on stale data for the length
   * of a request. This value reaches exactly two lines of text and nothing
   * else; `groupApi.get` stays the only authority for anything that matters.
   *
   * Read once, at mount: a conversation is always reached by `router.push`, so
   * the component is new every time — the same assumption the message seed and
   * `useThreadFollow` already rest on.
   */
  const [seededHeader] = useState(() => cachedGroupHeader(id));
  // Measurement only, for the intermittent keyboard overlap reported on build
  // 13 — see `use-keyboard-probe.ts`. Nothing here changes layout.
  const composerRef = useRef<View | null>(null);
  useKeyboardProbe(composerRef, "group");
  // Seeded from the cache, so reopening a group paints its last page on the
  // FIRST render rather than after two round trips. A group never opened
  // starts empty and pays the fetch, as before.
  const [messages, setMessages] = useState<SmallGroupMessage[]>(() =>
    cachedGroupMessages(id),
  );
  /**
   * What the list renders: the same messages, newest first, because the list
   * is `inverted`. The STATE stays oldest-first — every merge and send path
   * appends the way the API pages read — and only the render order flips.
   */
  /**
   * Messages the outbox still holds for this group — see `chat/[id].tsx`. They
   * render as ordinary bubbles at the live edge with a clock instead of a
   * timestamp, and they survive leaving the screen.
   */
  const enqueueOutbox = useOutboxStore((s) => s.enqueue);
  const pending = useOutboxStore((s) => s.entries);
  /**
   * Keys of messages already in the thread — see `chat/[id].tsx` for the full
   * reasoning. The group differs in ONE way and it does not change the answer:
   * this group's live broadcast excludes the sender (`group.service.ts` filters
   * `member.userId !== senderId`), so a socket echo of your own message never
   * arrives. A catch-up page after a reconnect still can, and that is the burst
   * that shakes hardest — the queue flushes at the same moment.
   */
  const withPending = useMemo<SmallGroupMessage[]>(
    () => [
      ...messages,
      ...visiblePending(
        id ? entriesForThread(pending, "group", id) : [],
        messages,
      ).map((entry) => ({
        id: entry.localId,
        groupId: entry.threadId,
        senderId: "me",
        senderName: t(strings.you),
        kind: "user" as const,
        content: entry.content,
        // A real timestamp, never the empty string the optimistic bubble used
        // to carry: the list has to be able to place it.
        timestamp: entry.createdAt,
        pending: true as const,
        pendingSlow: entry.slow,
        clientMessageId: entry.clientMessageId,
      })),
    ],
    [messages, pending, id, t],
  );

  const listData = useMemo(() => [...withPending].reverse(), [withPending]);

  // See `chat/[id].tsx`: the drawn row count must not change through a send.
  useEffect(() => {
    probe("thread.rows", {
      saved: messages.length,
      drawn: withPending.length,
    });
  }, [messages.length, withPending.length]);
  // Whether the thread reaches further back than what is held. Set from the
  // size of the last page: a SHORT page means the database ran out.
  const [hasOlder, setHasOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  // False until the first message read resolves. The empty-room placeholder
  // is gated on it: an empty ARRAY also describes a thread that simply has
  // not arrived yet, and showing "say hello" over a thread that exists reads
  // as a flash of the wrong screen every time the group is opened.
  // A cached page counts as loaded: it is real history, already rendered, so
  // the "say hello" placeholder must not appear over it.
  const [threadLoaded, setThreadLoaded] = useState(
    () => cachedGroupMessages(id).length > 0,
  );
  /**
   * Whether the first message read has landed, failed, or is still in flight —
   * the same three states the 1:1 thread carries, and for the same reason.
   *
   * A failed read used to be swallowed silently, which left the thread blank
   * with nothing to say why and no way to try again. Blank while PENDING is
   * deliberate (a spinner would flash on a warm connection); blank after a
   * FAILURE is just broken.
   */
  const [firstRead, setFirstRead] = useState<"pending" | "ready" | "failed">(
    () => (cachedGroupMessages(id).length > 0 ? "ready" : "pending"),
  );
  const [text, setText] = useState("");
  const [leaving, setLeaving] = useState(false);
  const [rejoining, setRejoining] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [meetupOpen, setMeetupOpen] = useState(false);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [customSpot, setCustomSpot] = useState("");
  const [meetupDraftActive, setMeetupDraftActive] = useState(false);
  const [reportMemberId, setReportMemberId] = useState<string | null>(null);
  /** Whose action menu is open. One at a time; null is closed. */
  const [menuMemberId, setMenuMemberId] = useState<string | null>(null);
  /** The member a Cue is in flight to, so the row can say so and refuse a second tap. */
  const [cueingMemberId, setCueingMemberId] = useState<string | null>(null);
  const [savingConsent, setSavingConsent] = useState(false);
  const [typingParticipants, setTypingParticipants] = useState<
    Record<string, TypingParticipant>
  >({});
  const listRef = useRef<FlatList<SmallGroupMessage>>(null);
  const { onScroll, onContentSizeChange, follow: followThread, maintainVisibleContentPosition } =
    useThreadFollow(listRef);
  const expiredHandledRef = useRef(false);
  const typingActiveRef = useRef(false);
  const lastTypingSentAtRef = useRef(0);
  const typingStopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const peerTypingTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>(
    {},
  );
  const inboxReturnTab =
    fromTab === "active" || fromTab === "inactive" ? fromTab : null;
  const returnToInbox = useCallback(() => {
    if (inboxReturnTab) {
      router.replace({
        pathname: "/(tabs)/inbox",
        params: { tab: inboxReturnTab },
      });
      return;
    }
    router.replace("/(tabs)/inbox");
  }, [inboxReturnTab, router]);

  const clearTypingStopTimer = useCallback(() => {
    if (typingStopTimerRef.current) {
      clearTimeout(typingStopTimerRef.current);
      typingStopTimerRef.current = null;
    }
  }, []);

  const clearPeerTypingTimers = useCallback(() => {
    Object.values(peerTypingTimersRef.current).forEach(clearTimeout);
    peerTypingTimersRef.current = {};
  }, []);

  const removeTypingParticipant = useCallback((userId: string) => {
    const timer = peerTypingTimersRef.current[userId];
    if (timer) {
      clearTimeout(timer);
      delete peerTypingTimersRef.current[userId];
    }
    setTypingParticipants((current) => {
      if (!current[userId]) return current;
      const next = { ...current };
      delete next[userId];
      return next;
    });
  }, []);

  const sendTypingState = useCallback(
    (isTyping: boolean) => {
      if (!id) return;
      typingActiveRef.current = isTyping;
      lastTypingSentAtRef.current = Date.now();
      void groupApi.typing(id, isTyping).catch(() => {});
    },
    [id],
  );

  const stopLocalTyping = useCallback(() => {
    clearTypingStopTimer();
    if (typingActiveRef.current) {
      sendTypingState(false);
    }
  }, [clearTypingStopTimer, sendTypingState]);

  const scheduleTypingStop = useCallback(() => {
    clearTypingStopTimer();
    typingStopTimerRef.current = setTimeout(() => {
      stopLocalTyping();
    }, TYPING_STOP_MS);
  }, [clearTypingStopTimer, stopLocalTyping]);

  const handleTextChange = useCallback(
    (next: string) => {
      setText(next);

      if (!next.trim()) {
        stopLocalTyping();
        return;
      }

      const now = Date.now();
      if (
        !typingActiveRef.current ||
        now - lastTypingSentAtRef.current > TYPING_REFRESH_MS
      ) {
        sendTypingState(true);
      }

      scheduleTypingStop();
    },
    [scheduleTypingStop, sendTypingState, stopLocalTyping],
  );

  /**
   * Re-read the group itself — members, meetup, ready, expiry, and whether the
   * viewer is still in it.
   *
   * Returns whether there is still a thread worth reading, so callers that go on
   * to fetch messages do not do so for a group the viewer has been removed from
   * or that has ended.
   */
  const refreshGroupState = useCallback(async (): Promise<boolean> => {
    if (!id) return false;
    try {
      const nextGroup = await groupApi.get(id);
      setGroup(nextGroup);
      // The authoritative read is also the best thing to remember, so a
      // reopen starts from the truth rather than from a list row.
      rememberGroupHeader(id, nextGroup);
      if (
        nextGroup.viewerMembershipStatus === "host_removed" ||
        nextGroup.viewerMembershipStatus === "removed_for_safety"
      ) {
        // The thread STAYS. This used to empty it, which was the client half of
        // a rule the server has since reversed: being removed ends your part in
        // the group, it does not retract the conversation you were already in.
        // What is dropped is the half-typed reply and the meetup draft, because
        // neither can be sent any more.
        setText("");
        setMeetupDraftActive(false);
        return true;
      }
      return true;
    } catch (err) {
      // Only a 404 means the group is genuinely over (expired or everyone
      // left). Transient failures — a network blip, a token refresh, or the
      // venue momentarily not resolving from GPS (400/403) — must NOT kick the
      // user out with an "ended" popup; keep the last state and retry on the
      // next poll.
      if (!(err instanceof ApiError) || err.status !== 404) return false;
      if (expiredHandledRef.current) return false;
      expiredHandledRef.current = true;
      notify(t(strings.groupEndedTitle), t(strings.groupEndedBody));
      returnToInbox();
      return false;
    }
  }, [id, returnToInbox, t]);

  /** The full read: the group and its whole thread. Mount and reconnect only. */
  /**
   * Synchronous, unlike `loadingOlder`.
   *
   * `loadingOlder` is React state and the scroll handler runs at 60fps, so the
   * value it reads does not change until React commits — one flick therefore
   * issued one request per frame, every one of them carrying the SAME `before`
   * cursor. `mergeMessages` hid that on this thread by deduping on id; the
   * practice transcript prepends its page directly and duplicated turns.
   *
   * The same latch, against the same mistake, as the auth submits in
   * `lib/single-tap.ts`. The state flag stays, but only to draw the header.
   */
  const olderLatch = useRef(createLatch()).current;

  /**
   * The page BEFORE the oldest message held.
   *
   * Opening the thread reads the newest page; this is how the rest of it is
   * reached. Without it, bounding that read would make older messages
   * unreachable rather than merely unloaded.
   */
  const loadOlder = useCallback(async () => {
    if (!id) return;
    const oldest = messages.find((message) => !message.id.startsWith("local-"));
    if (!oldest) return;

    if (!olderLatch.claim()) return;

    setLoadingOlder(true);
    try {
      const older = await groupApi.messages(id, {
        before: oldest.id,
        limit: CATCH_UP_PAGE,
      });
      setMessages((prev) => mergeMessages(prev, older));
      setHasOlder(moreBehind(older.length, CATCH_UP_PAGE));
    } catch {
      // Left as it was, so scrolling back up simply tries again.
    } finally {
      setLoadingOlder(false);
      olderLatch.release();
    }
  }, [id, messages, olderLatch]);

  /**
   * Reaching back, decided by the list reporting its older end — the scroll
   * END of an inverted thread, delivered as `onEndReached`. The guards are
   * `lib/load-older.ts`, shared with the practice and one-to-one threads so
   * the three stop diverging; the `hasOlder` guard is what stops this asking
   * for the page before the first message for ever once the start is reached.
   */
  const maybeLoadOlder = useCallback(() => {
    const anchored = messages.some(
      (message) => !message.id.startsWith("local-"),
    );
    if (
      !canLoadOlder({
        loading: olderLatch.held,
        hasOlder,
        hasAnchor: anchored,
      })
    ) {
      return;
    }
    void loadOlder();
  }, [hasOlder, loadOlder, messages, olderLatch]);

  const load = useCallback(async () => {
    if (!id) return;
    // In PARALLEL, not in sequence. These two reads are independent — the
    // message page does not depend on the group's state — and awaiting them
    // one after the other made opening a group cost two round trips of blank
    // thread. The group read still decides whether the thread is worth
    // showing; a message page fetched for a group that turns out to be gone is
    // simply discarded, which is one wasted request in a rare case against a
    // halved wait in the common one.
    const endLoad = probeSpan("group.thread.load");
    const [stillThere, nextMessages] = await Promise.all([
      refreshGroupState(),
      // The NEWEST page, not the whole thread. A group runs for up to 48
      // hours and an ended one stays readable for ever, so this read used to
      // grow without any limit at all.
      groupApi.messages(id, { limit: CATCH_UP_PAGE }).catch(() => null),
    ]);
    endLoad();
    if (!stillThere) return;
    // A message read that fails is not an ending — only the group read decides
    // that, and it comes round again on the state poll. But it must SAY so:
    // swallowing it left a permanently blank thread.
    if (!nextMessages) {
      setFirstRead((current) => (current === "pending" ? "failed" : current));
      return;
    }
    setMessages(nextMessages);
    setHasOlder(moreBehind(nextMessages.length, CATCH_UP_PAGE));
    setThreadLoaded(true);
    setFirstRead("ready");
    void groupApi.markRead(id).catch(() => {});
  }, [id, refreshGroupState]);

  /**
   * Fetch only the messages that arrived while we were not listening.
   *
   * Reads forward from the last message we hold, so the cost is the size of the
   * gap rather than the size of the thread. With nothing held there is no cursor,
   * so it falls back to the full read `load` does on mount.
   */
  const catchUpMessages = useCallback(async () => {
    if (!id) return;
    try {
      let cursor = lastServerMessageId(messages);

      if (!cursor) {
        const all = await groupApi.messages(id, { limit: CATCH_UP_PAGE });
        setMessages((prev) => mergeMessages(prev, all));
        setHasOlder(moreBehind(all.length, CATCH_UP_PAGE));
        return;
      }

      for (let page = 0; page < CATCH_UP_MAX_PAGES; page += 1) {
        const batch = await groupApi.messages(id, {
          after: cursor,
          limit: CATCH_UP_PAGE,
        });
        if (batch.length === 0) return;

        setMessages((prev) => mergeMessages(prev, batch));
        void groupApi.markRead(id).catch(() => {});

        // A short page means the gap is closed; a full one means there may be
        // more behind it, so walk on from the last id we just took.
        if (batch.length < CATCH_UP_PAGE) return;
        const next = batch[batch.length - 1]?.id;
        if (!next || next === cursor) return;
        cursor = next;
      }
    } catch {
      // A transient failure is not an ending — the next event or poll retries.
    }
  }, [id, messages]);

  // Reached through a ref so the socket effect can use it without taking it as a
  // dependency: `catchUpMessages` closes over `messages` for its cursor, so its
  // identity changes with every message that arrives, and depending on it would
  // rebuild the connection each time.
  const catchUpRef = useRef(catchUpMessages);
  useEffect(() => {
    catchUpRef.current = catchUpMessages;
  }, [catchUpMessages]);

  useEffect(() => {
    const initialTimer = setTimeout(() => {
      void load();
    }, 0);
    return () => clearTimeout(initialTimer);
  }, [load]);

  /**
   * A slow poll of the group's own state — members, meetup, ready, expiry.
   *
   * This used to be a 2.5s re-read of the group AND every message in it. The
   * messages now arrive over the socket and gaps are closed with a cursor, so
   * only the state is left, and that payload is small and bounded by the size of
   * the group.
   *
   * It is still a poll because joining, suggesting a meetup, and marking ready
   * are not broadcast — only messages, removals, and typing are. Until those emit
   * too, this is how the rest of the group finds out.
   */
  useEffect(() => {
    if (!id) return undefined;
    const timer = setInterval(() => {
      void refreshGroupState();
    }, GROUP_STATE_POLL_MS);
    return () => clearInterval(timer);
  }, [id, refreshGroupState]);

  // Returning from the background: the socket was torn down while the phone
  // slept and reconnects a moment later, but the thread and the group's state
  // may both have moved on.
  useEffect(() => {
    if (!id) return undefined;
    const subscription = AppState.addEventListener("change", (state) => {
      if (state !== "active") return;
      void catchUpRef.current();
      void refreshGroupState();
    });
    return () => subscription.remove();
  }, [id, refreshGroupState]);

  // Listens on the app-wide connection rather than opening one. A second
  // `/events` socket is not a shared one — socket.io-client builds a fresh
  // Manager for a namespace it already holds — so this screen used to cost a
  // second WebSocket, a second JWT handshake, and a second copy of every
  // user-targeted event for as long as it stayed mounted.
  useEffect(() => {
    if (!id || USE_MOCK) return undefined;

    const unsubscribe = eventsBus.subscribe({
      // A reconnect means both may have moved on without us — the thread and
      // the group around it. Each is closed the cheap way: the messages from our
      // cursor, the group by its own small read.
      connect: () => {
        void catchUpRef.current();
        void refreshGroupState();
      },
      // A new message needs only the messages, and only the ones behind our
      // cursor — this is the hot path, and it used to re-read the entire thread.
      "group:message": (event) => {
        if (event.groupId === id) {
          void catchUpRef.current();
        }
      },
      // Membership changes are about the group, not the thread.
      "group:members": (event) => {
        if (event.groupId === id) {
          void refreshGroupState();
        }
      },
      "group:removed": (event) => {
        if (event.groupId === id) {
          void refreshGroupState();
        }
      },
      "group:typing": (event) => {
        if (event.groupId !== id || event.userId === currentUserId) return;

        if (!event.isTyping) {
          removeTypingParticipant(event.userId);
          return;
        }

        setTypingParticipants((current) => ({
          ...current,
          [event.userId]: {
            userId: event.userId,
            displayName: event.displayName || t(strings.someone),
          },
        }));

        const existingTimer = peerTypingTimersRef.current[event.userId];
        if (existingTimer) clearTimeout(existingTimer);
        peerTypingTimersRef.current[event.userId] = setTimeout(() => {
          removeTypingParticipant(event.userId);
        }, PEER_TYPING_TTL_MS);
      },
    });

    return () => {
      // Every handler this effect registered, removed in one go. On a shared
      // connection a forgotten `off` is a listener that accumulates per visit.
      unsubscribe();
      clearPeerTypingTimers();
      setTypingParticipants({});
    };
  }, [
    clearPeerTypingTimers,
    currentUserId,
    id,
    refreshGroupState,
    removeTypingParticipant,
    t,
  ]);

  // Mark this group as the one being read so its messages clear the unread
  // badge and don't trigger notifications while it's open.
  useEffect(() => {
    if (!id) return undefined;
    setActiveGroup(id);
    markGroupRead(id);
    return () => setActiveGroup(null);
  }, [id, setActiveGroup, markGroupRead]);

  /**
   * Keep the cache current, so the NEXT open paints instantly. Pending bubbles
   * are excluded: they are the outbox's, they are already rendered from it,
   * and storing them would show a message twice once it lands.
   */
  useEffect(() => {
    if (!id) return;
    rememberGroupMessages(
      id,
      messages.filter((message) => !message.id.startsWith("outbox-")),
    );
  }, [id, messages]);

  /**
   * A queued message reached the server — merge it and move the inbox row with
   * the SERVER's timestamp. See `chat/[id].tsx`; the difference here is that a
   * group send has no socket echo at all (`notifyGroupMessage` filters the
   * sender out), so this is the only thing that moves the row.
   */
  useEffect(() => {
    if (!id) return;
    return onOutboxSent((threadKind, threadId, saved) => {
      if (threadKind !== "group" || threadId !== id) return;
      const message = saved as SmallGroupMessage;
      setMessages((prev) => mergeMessages(prev, [message]));
      receiveGroupMessage(id, {
        preview: message.content,
        sentAt: message.timestamp,
      });
    });
  }, [id, receiveGroupMessage]);


  useEffect(() => {
    return () => {
      stopLocalTyping();
      clearPeerTypingTimers();
    };
  }, [clearPeerTypingTimers, stopLocalTyping]);

  async function send() {
    const body = text.trim();
    if (!id || !body) return;
    stopLocalTyping();
    if (meetupDraftActive) {
      // The meetup branch clears its own text, because it still owns the
      // failure: it restores the draft when the suggestion does not land.
      setText("");
      setMeetupDraftActive(false);
      // The sender asked to see their own suggestion — the same rule as the
      // ordinary path below. Without it, a reader who had scrolled up sent a
      // meetup that landed off-screen with nothing to say it had.
      followThread();
      // Its OWN span name, never `group.send.*`: this branch paints nothing
      // until the server responds — no optimistic bubble, no optimistic→saved
      // swap — so folding it into the send bracket would mix two different
      // shapes into the one number the device session compares.
      const endMeetupRequest = probeSpan("group.meetup.request");
      try {
        // "Custom" stays capitalised: this is the WIRE value, and
        // `group.controller.ts` compares `dto.spot === "Custom"` before
        // wrapping the body into a system message. It is deliberately NOT the
        // local `MEETUP_SPOTS` enum — changing it here without the server
        // would make every suggestion post the literal word instead.
        const saved = await groupApi.suggestMeetup(id, "Custom", body);
        endMeetupRequest();
        setMessages((prev) => [...prev, saved]);
        // A meetup suggestion is a real message in the thread, so it moves
        // the inbox row like one — with the server's values, same as below.
        receiveGroupMessage(id, {
          preview: saved.content,
          sentAt: saved.timestamp,
        });
      } catch {
        endMeetupRequest();
        setText(body);
        setMeetupDraftActive(true);
        notify(t(strings.groupUpdateFailedTitle), t(strings.tryAgain));
        void load();
      }
      return;
    }
    // Hand it to the outbox, which owns it from here — see `chat/[id].tsx`.
    // What this replaces was the noisiest failure in the app: the bubble was
    // removed, the text pushed back into the composer, a generic "could not
    // update group" alert raised, and the whole thread re-read — four
    // reactions to one dropped packet, none of which the user could act on.
    const result = enqueueOutbox({
      threadKind: "group",
      threadId: id,
      content: body,
    });

    if (!result.accepted) {
      notify(t(strings.messageQueueFullTitle), t(strings.messageQueueFullBody));
      return;
    }

    setText("");
    followThread();
  }

  /**
   * Share a link to this group.
   *
   * The link is a POINTER, never a grant: it carries the group id and nothing
   * else, and whoever opens it is still checked against the group's own range
   * before they can join. That is why it is safe to forward — and why the
   * landing screen shows the group to someone standing too far away, rather
   * than pretending it does not exist.
   */
  /**
   * Change the join range. Host only, and enforced server-side — this is the
   * convenience, not the control.
   *
   * Narrowing takes people OUT of range for joining but never removes anyone
   * already inside: they were admitted under the old range, and ejecting
   * someone mid-conversation is a worse surprise than a range that is briefly
   * historical.
   */
  async function commitRadius(radiusM: number) {
    try {
      const updated = await groupApi.setRadius(id, radiusM);
      setGroup(updated);
    } catch (err) {
      // The server's own words when it gave any — a title over an empty body
      // is how a refused range read as "nothing happened, no reason given".
      notify(
        t(strings.groupUpdateFailedTitle),
        err instanceof Error && err.message ? err.message : t(strings.tryAgain),
      );
    }
  }

  async function shareInvite() {
    const url = `${publicConfig.webBaseUrl}/group/join/${id}`;
    try {
      await shareText(
        Platform.OS === "ios"
          ? { url, message: t(strings.inviteShareMessage, { name: group?.name ?? "" }) }
          : {
              message: `${t(strings.inviteShareMessage, { name: group?.name ?? "" })} ${url}`,
            },
      );
    } catch {
      // A dismissed share sheet rejects on some platforms; that is a
      // cancellation, not a failure, and must not raise an error to the user.
    }
  }

  async function deleteEndedGroup() {
    const confirmed = await confirm({
      title: t(strings.endedDeleteConfirmTitle),
      confirmLabel: t(strings.endedDeleteConfirmAction),
    });
    if (!confirmed) return;
    try {
      expiredHandledRef.current = true;
      await groupApi.delete(id);
      returnToInbox();
    } catch {
      expiredHandledRef.current = false;
      notify(t(strings.groupUpdateFailedTitle), t(strings.tryAgain));
    }
  }

  /**
   * Walk back into a group you left, while it is still running.
   *
   * Nothing is recovered by doing this: the conversation that happened while
   * they were away was withheld when it was written and stays withheld (see
   * `small_group_messages.hidden_from`). Coming back reopens the composer, it
   * does not reopen the past — the same promise `resume()` makes on a
   * one-to-one thread.
   */
  async function rejoin() {
    if (!id || rejoining) return;
    setRejoining(true);
    try {
      // No intent passed, so the server reads it as Join only. Coming back is
      // ARRIVING again — the same reading that makes the age range and the join
      // radius gate arrival — and a permission granted before somebody walked
      // out is not a permission they have granted now. It is one tap to turn
      // back on from the roster, and that switch is where it belongs: a rejoin
      // that silently restored it would restore a decision nobody re-made.
      await groupApi.join(id);
      await load();
    } catch (err) {
      notify(
        t(strings.groupUpdateFailedTitle),
        err instanceof ApiError && err.status === 403
          ? t(strings.inviteRemovedTitle)
          : t(strings.tryAgain),
      );
    } finally {
      setRejoining(false);
    }
  }

  async function leave() {
    if (!id || leaving) return;
    const ok = await confirm({
      title: t(strings.leaveConfirmTitle),
      message:
        group?.hostUserId === currentUserId
          ? t(strings.leaveHostConfirmBody)
          : t(strings.leaveConfirmBody),
      confirmLabel: t(strings.leaveConfirmAction),
      cancelLabel: t(strings.cancel),
      destructive: true,
    });
    if (!ok) return;
    setLeaving(true);
    try {
      await groupApi.leave(id);
      returnToInbox();
      // Where it went, said out loud. The thread is not gone — it moves to
      // Inactive, read-only, and stays rejoinable while the group runs — and a
      // list they are no longer in is the last place that explains itself. The
      // one-to-one quiet exit says the same thing the same way.
      notify(t(strings.groupLeftTitle), t(strings.groupLeftNotice));
      // Leaving a Cue Circle is a reflection moment.
      requestReflection("circle");
    } finally {
      setLeaving(false);
    }
  }

  async function suggestMeetup(spot: MeetupSpotValue) {
    const custom = customSpot.trim();
    if (spot === "custom" && !custom) return;
    setMeetupOpen(false);
    setCustomSpot("");
    const entry = MEETUP_SPOTS.find((item) => item.value === spot);
    if (!entry) return;
    // Only `custom` interpolates; the others ignore the argument, and passing
    // it unconditionally keeps the branch out of the call.
    const draft = t(entry.draftKey, { spot: custom });
    await applyMeetupDraft(draft);
  }

  const visibleTypingParticipants = Object.values(typingParticipants).slice(
    0,
    3,
  );
  const removedFromGroup =
    group?.viewerMembershipStatus === "host_removed" ||
    group?.viewerMembershipStatus === "removed_for_safety";
  /** They walked out themselves, and the group is carrying on without them. */
  const leftGroup = group?.viewerMembershipStatus === "left";
  /**
   * Is this thread finished FOR ME? The server answers `state` per viewer now,
   * so a running group reads `inactive` for someone who left or was removed.
   * Everything that must not be done to a finished thread hangs off this one
   * flag rather than three that can disagree.
   *
   * The membership statuses are still tested alongside it, deliberately: a
   * client running against an API built before viewer-relative state would see
   * `active` here, and the composer must close for a removed member either way.
   * Absent state reads as active, which only ever errs toward the old
   * behaviour.
   */
  const ended = (group?.state ?? "active") === "inactive";
  const readOnly = ended || removedFromGroup || leftGroup;
  /**
   * The GROUP is over, as opposed to my own part in it being over — the one
   * distinction `state` alone can no longer make now that it is per-viewer.
   * Only the banner needs it; every gate above wants `ended`.
   */
  const groupOver = ended && !leftGroup && !removedFromGroup;
  const isHost = group?.hostUserId === currentUserId;
  /**
   * The viewer's own row in the roster, which is where their own cue consent
   * lives. Read from the roster rather than kept in separate state so the
   * switch can never disagree with the list beneath it — one source, and it is
   * the one the server just sent.
   */
  const viewerMembership = group?.members.find(
    (member) => member.id === currentUserId,
  );
  const menuMember =
    menuMemberId === null
      ? undefined
      : group?.members.find((member) => member.id === menuMemberId);
  const showExpiryWarning =
    !!group && !readOnly && group.minutesLeft > 0 && group.minutesLeft <= 10;

  async function applyMeetupDraft(draft: string) {
    if (text.trim()) {
      const replace = await confirm({
        title: t(strings.meetupDraftConflictTitle),
        confirmLabel: t(strings.meetupDraftConflictReplace),
        cancelLabel: t(strings.meetupDraftConflictCancel),
      });
      if (!replace) return;
    }
    setText(draft);
    setMeetupDraftActive(true);
  }

  async function removeMember(memberId: string) {
    if (!id) return;
    const ok = await confirm({
      title: t(strings.removeConfirmTitle),
      message: t(strings.removeConfirmBody),
      confirmLabel: t(strings.removeConfirmAction),
      cancelLabel: t(strings.cancel),
      destructive: true,
    });
    if (!ok) return;
    try {
      await groupApi.removeMember(id, memberId);
      notify(t(strings.removeSuccess));
      await load();
    } catch {
      notify(t(strings.groupUpdateFailedTitle), t(strings.tryAgain));
    }
  }

  /**
   * Cue somebody from this roster.
   *
   * `groupId` is passed so the server knows which SURFACE the tap came from —
   * it is not a claim of permission. The server re-reads both memberships and
   * the recipient's consent before it accepts anything, so the roster offering
   * the action and the server allowing it are two separate judgements and this
   * one is only the first.
   *
   * That is why the failure path shows the server's own words. The roster
   * answers the group question only; blocks, pair cooldowns and conversation
   * history are facts about the VIEWER that a roster must never disclose, so
   * they arrive here as a refusal rather than as a greyed-out row — a Cue
   * action that dimmed for a block would tell the blocker's target they had
   * been blocked.
   */
  async function cueMember(member: SmallGroupMember) {
    if (!id || cueingMemberId) return;
    setCueingMemberId(member.id);
    try {
      await cuesApi.send(member.id, { groupId: id });
      notify(t(strings.memberCueSent, { name: member.displayName }));
    } catch (err) {
      notify(
        t(strings.groupUpdateFailedTitle),
        err instanceof Error && err.message ? err.message : t(strings.tryAgain),
      );
    } finally {
      setCueingMemberId(null);
    }
  }

  /**
   * Change your mind about being cued by this group's members.
   *
   * Turning it OFF withdraws nothing already sent. A pending Cue is a question
   * that has been asked; the sender may cancel it and the recipient still has
   * Accept, Ignore and Block on the card. The copy under the switch says so,
   * because a toggle that looks like it revokes and does not is worse than one
   * that never claimed to.
   */
  async function setCueConsent(next: boolean) {
    if (!id || savingConsent) return;
    setSavingConsent(true);
    try {
      setGroup(await groupApi.setCueConsent(id, next));
    } catch (err) {
      // The switch lives in the members sheet; the error is a dialog, and a
      // dialog cannot present over the sheet's native modal — close it first.
      setMembersOpen(false);
      notify(
        t(strings.groupUpdateFailedTitle),
        err instanceof Error && err.message ? err.message : t(strings.tryAgain),
      );
    } finally {
      setSavingConsent(false);
    }
  }

  /**
   * Run one roster action.
   *
   * ONE dispatcher for both shapes the action can take — the bare icon on a
   * single-action row, and the row inside the menu. Duplicating the handlers
   * per shape is how the collapsed form ends up doing something subtly
   * different from the expanded one.
   */
  function runMemberAction(kind: MemberActionKind, member: SmallGroupMember) {
    if (kind === "message") {
      if (!member.chatId) return;
      setMenuMemberId(null);
      setMembersOpen(false);
      router.push(`/chat/${member.chatId}`);
      return;
    }
    if (kind === "cue") {
      // Closed BEFORE the send, not after it succeeds: the result arrives as
      // a dialog either way, and a dialog cannot present while a sheet's
      // native modal is still up (the swap-never-stack rule at the menu
      // opener). The old order left the sheet open on failure, so the error
      // notice wedged instead of showing.
      setMenuMemberId(null);
      setMembersOpen(false);
      void cueMember(member);
      return;
    }
    if (kind === "report") {
      setMenuMemberId(null);
      setMembersOpen(false);
      setReportMemberId(member.id);
      return;
    }
    // Remove confirms first, and the confirm is a dialog too — same rule.
    setMenuMemberId(null);
    setMembersOpen(false);
    void removeMember(member.id);
  }

  function memberActionLabel(kind: MemberActionKind): string {
    if (kind === "message") return t(strings.memberMessage);
    if (kind === "cue") return t(strings.memberCue);
    if (kind === "report") return t(strings.reportMember);
    return t(strings.removeMember);
  }

  function openMemberProfile(memberId: string) {
    setMembersOpen(false);
    if (memberId === currentUserId || memberId === "me") {
      router.push("/(tabs)/profile");
      return;
    }
    router.push(`/guest-profile?v=${handleFor(memberId)}`);
  }

  return (
    <SafeAreaView
      style={[styles.safe, { backgroundColor: c.background }]}
      edges={["top"]}
    >
      <View
        style={[
          styles.header,
          Platform.OS === "web" && styles.headerWeb,
          { borderBottomColor: c.border },
        ]}
      >
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t(strings.backHomeA11y)}
          onPress={returnToInbox}
          hitSlop={10}
          style={styles.back}
        >
          <Ionicons name="chevron-back" size={26} color={c.text} />
        </Pressable>

        <View style={styles.headerCopy}>
          {/* Nothing at all until we know the group, rather than a placeholder
              name that is then replaced.
              
              This used to read "Small Group" / "Temporary group nearby" and
              swap once `groupApi.get` answered, which is the flash reported on
              build 13. Seeding from `group-cache.ts` removes it for every
              normal route in, and for the ones that are left — a deep link, a
              push — the honest answer is silence: the header appears once, in
              its final form, alongside the thread. A placeholder would only
              move the flash to the paths hardest to test.
              
              The header does not collapse while this is empty. `styles.header`
              carries `minHeight: 62` and a 40pt options button, and centres its
              row, so the bar keeps its size and the copy fades in inside it. */}
          {group?.name || seededHeader?.name ? (
            <ThemedText variant="bodyStrong" numberOfLines={1}>
              {group?.name ?? seededHeader?.name}
            </ThemedText>
          ) : null}
          {/* Count + time only — the goal lives in the band below, so this
              line survives large font settings and longer languages without
              truncating the numbers that actually change. */}
          {group ? (
            <ThemedText variant="caption" tone="muted" numberOfLines={1}>
              {t(strings.groupSummary, {
                count: group.memberCount,
                max: group.maxMembers,
                minutes: group.minutesLeft,
              })}
            </ThemedText>
          ) : seededHeader ? (
            <ThemedText variant="caption" tone="muted" numberOfLines={1}>
              {t(strings.groupSummary, {
                count: seededHeader.memberCount,
                max: seededHeader.maxMembers,
                // DERIVED, never a remembered countdown — see
                // `GroupHeaderSnapshot.expiresAt`.
                minutes: minutesLeftUntil(seededHeader.expiresAt),
              })}
            </ThemedText>
          ) : null}
        </View>

        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t(strings.groupOptionsA11y)}
          disabled={removedFromGroup}
          onPress={() => setOptionsOpen(true)}
          style={[
            styles.optionsButton,
            { backgroundColor: c.backgroundSelected },
            removedFromGroup && styles.disabled,
          ]}
        >
          <Ionicons name="ellipsis-horizontal" size={22} color={c.tint} />
        </Pressable>
      </View>

      {group?.vibe ? (
        <View style={[styles.goalBand, { borderBottomColor: c.border }]}>
          <ThemedText variant="caption" numberOfLines={1}>
            {group.vibe}
          </ThemedText>
        </View>
      ) : null}

      {showExpiryWarning ? (
        <View
          style={[
            styles.expiryBanner,
            { backgroundColor: c.surfaceAlt, borderBottomColor: c.border },
          ]}
        >
          <Ionicons name="time-outline" size={16} color={Palette.amber} />
          <ThemedText variant="caption" style={{ color: Palette.amber }}>
            {group.minutesLeft <= 1
              ? t(strings.endsSoon)
              : t(strings.endsIn, { count: group.minutesLeft })}
          </ThemedText>
        </View>
      ) : null}

      <KeyboardAvoidingContainer
        style={styles.flex}
        keyboardVerticalOffset={8}
      >
        <FlatList
          ref={listRef}
          // Inverted: the newest message is item zero and renders at the
          // visual bottom, so the FIRST painted frame of the thread is its
          // latest message. This replaced landing there by scrolling after
          // layout, which painted the top of the page first — captured on
          // device as the thread visibly opening on its oldest loaded message
          // before jumping.
          inverted
          data={listData}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.messages}
          showsVerticalScrollIndicator={false}
          onScroll={onScroll}
          scrollEventThrottle={16}
          onContentSizeChange={onContentSizeChange}
          // A new message inserts at item zero — the scroll START — which
          // shifts a reader who is anywhere else by the height of that row.
          // Native anchors them through this; the web build relies on browser
          // scroll anchoring instead (measured holding the reader's row to the
          // pixel). Loading older history APPENDS in an inverted list and
          // shifts nobody.
          // The hook's value, not a literal: a follower is never anchored —
          // anchoring them painted their own send low. See `use-thread-follow.ts`.
          maintainVisibleContentPosition={maintainVisibleContentPosition}
          // The older end of an inverted thread is the scroll end.
          onEndReached={maybeLoadOlder}
          onEndReachedThreshold={0.5}
          ListFooterComponent={
            // The FOOTER of an inverted list renders at the visual top, which
            // is the older end — where the page being fetched will appear.
            loadingOlder ? (
              <View style={styles.loadingOlder}>
                <ThemedText variant="caption" tone="muted" center>
                  {t(strings.loadingEarlier)}
                </ThemedText>
              </View>
            ) : null
          }
          renderItem={({ item }) => (
            <GroupMessageBubble
              message={item}
              mine={item.senderId === "me" || item.senderId === currentUserId}
            />
          )}
          ListEmptyComponent={
            // Blank while the first read is in flight — the header, members
            // and composer are already drawn, so the screen does not read as
            // broken, and nothing claims the group is empty before anyone
            // knows. A failure, though, is actionable and says so.
            firstRead === "failed" ? (
              <Pressable
                accessibilityRole="button"
                style={styles.empty}
                onPress={() => {
                  setFirstRead("pending");
                  void load();
                }}
              >
                <ThemedText variant="body" tone="muted" center>
                  {t(strings.messagesUnavailable)}
                </ThemedText>
              </Pressable>
            ) : threadLoaded ? (
              <View style={styles.empty}>
                <ThemedText variant="body" tone="muted" center>
                  {t(strings.groupEmpty)}
                </ThemedText>
              </View>
            ) : null
          }
        />

        <GroupTypingIndicator
          participants={visibleTypingParticipants}
          colors={c}
        />

        {groupOver ? (
          <View
            accessibilityRole="alert"
            style={[styles.removedCard, { borderColor: c.border }]}
          >
            <ThemedText variant="caption" tone="muted" center>
              {t(strings.endedBanner)}
            </ThemedText>
          </View>
        ) : null}

        {leftGroup ? (
          <View
            accessibilityRole="alert"
            style={[styles.removedCard, { borderColor: c.border }]}
          >
            <ThemedText variant="bodyStrong" center>
              {t(strings.groupLeftTitle)}
            </ThemedText>
            <ThemedText variant="caption" tone="muted" center>
              {t(strings.groupLeftBody)}
            </ThemedText>
            {/* Only while the group is still running. Rejoining an ended one is
                refused by the server, and offering a button that cannot work is
                worse than not offering one. */}
            {group && group.minutesLeft > 0 ? (
              <Pressable
                accessibilityRole="button"
                disabled={rejoining}
                onPress={() => void rejoin()}
                style={[styles.removedButton, { backgroundColor: c.tint }]}
              >
                <ThemedText variant="caption" tone="onTint">
                  {t(strings.groupLeftRejoin)}
                </ThemedText>
              </Pressable>
            ) : null}
          </View>
        ) : null}

        {removedFromGroup ? (
          <View
            accessibilityRole="alert"
            style={[styles.removedCard, { borderColor: c.border }]}
          >
            <ThemedText variant="bodyStrong" center>
              {t(strings.removedTitle)}
            </ThemedText>
            <ThemedText variant="caption" tone="muted" center>
              {t(strings.removedBody)}
            </ThemedText>
            <Pressable
              accessibilityRole="button"
              onPress={returnToInbox}
              style={[styles.removedButton, { backgroundColor: c.tint }]}
            >
              <ThemedText variant="caption" tone="onTint">
                {t(strings.removedClose)}
              </ThemedText>
            </Pressable>
            <Pressable
              accessibilityRole="button"
              onPress={() => router.push("/help/report-problem?source=report")}
              style={[styles.removedButton, { borderColor: c.border, borderWidth: 1 }]}
            >
              <ThemedText variant="caption" tone="tint">
                {t(strings.removedReport)}
              </ThemedText>
            </Pressable>
          </View>
        ) : null}

        <View
          ref={composerRef}
          style={[
            styles.inputBar,
            Platform.OS === "web" && styles.inputBarWeb,
          ]}
        >
          <TextInput
            value={text}
            onChangeText={handleTextChange}
            editable={!readOnly}
            placeholder={t(strings.groupMessagePlaceholder)}
            placeholderTextColor={c.textMuted}
            style={[
              styles.input,
              {
                color: c.text,
                backgroundColor: c.surface,
                borderColor: c.border,
              },
            ]}
            multiline
            maxLength={GROUP_MESSAGE_MAX_LENGTH}
            returnKeyType="send"
            onSubmitEditing={send}
          />
          <Pressable
            onPress={send}
            disabled={!text.trim() || readOnly}
            style={[
              styles.sendBtn,
              { backgroundColor: c.tint },
              // Dimmed, not recoloured. Swapping the fill to `c.border` made
              // the group composer read as a different control from the one in
              // Chat, which keeps the tint and drops opacity.
              (!text.trim() || readOnly) && styles.disabled,
            ]}
          >
            <Ionicons name="arrow-up" size={20} color={c.onTint} />
          </Pressable>
        </View>
      </KeyboardAvoidingContainer>

      <Modal
        visible={optionsOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setOptionsOpen(false)}
      >
        <View style={styles.modalScreen}>
          <BlurredBackdrop
            accessibilityLabel={t(strings.closeGroupOptionsA11y)}
            style={styles.modalBackdrop}
            onPress={() => setOptionsOpen(false)}
          />
          <View
            style={[
              styles.actionSheet,
              { backgroundColor: c.surface, borderColor: c.border },
            ]}
          >
            <GroupAction
              icon="people-outline"
              label={t(strings.menuMembers)}
              onPress={() => {
                setOptionsOpen(false);
                setMembersOpen(true);
              }}
            />
            {isHost && !ended ? (
              <GroupAction
                icon="navigate-outline"
                label={t(strings.menuRange)}
                onPress={() => {
                  setOptionsOpen(false);
                  setRangeOpen(true);
                }}
              />
            ) : null}
            {/* Invite. Members only — the sheet is only reachable from inside
                a group — and the link is just a pointer: whoever opens it must
                still be inside the group's own range to join. */}
            {!ended ? (
              <>
                <GroupAction
                  icon="person-add-outline"
                  label={t(strings.menuInvite)}
                  onPress={() => {
                    setOptionsOpen(false);
                    void shareInvite();
                  }}
                />
                <GroupAction
                  icon="location-outline"
                  label={t(strings.menuSuggestMeetupSpot)}
                  onPress={() => {
                    setOptionsOpen(false);
                    setMeetupOpen(true);
                  }}
                />
              </>
            ) : null}
            <View style={[styles.sheetDivider, { backgroundColor: c.border }]} />
            {!ended ? (
              <GroupAction
                icon="exit-outline"
                label={t(strings.menuLeaveGroup)}
                destructive
                onPress={() => {
                  setOptionsOpen(false);
                  void leave();
                }}
              />
            ) : (
              /* Delete replaces Leave once the group is over: leaving history
                 makes no sense, and deleting a LIVE group is refused
                 server-side — Leave is the exit while it runs. Per-member:
                 nobody else's history moves. */
              <GroupAction
                icon="trash-outline"
                label={t(strings.menuDeleteGroup)}
                destructive
                onPress={() => {
                  setOptionsOpen(false);
                  void deleteEndedGroup();
                }}
              />
            )}
            <GroupAction
              icon="flag-outline"
              label={t(strings.menuReportGroup)}
              destructive
              onPress={() => {
                setOptionsOpen(false);
                router.push("/help/report-problem?source=report");
              }}
            />
          </View>
        </View>
      </Modal>

      {/* Host-only range editor. The slider commits on release through the
          same clamp the server applies, so the two can never disagree about
          what the host asked for. */}
      <Modal
        visible={rangeOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setRangeOpen(false)}
      >
        <View style={styles.modalScreen}>
          <BlurredBackdrop
            accessibilityLabel={t(strings.closeGroupOptionsA11y)}
            onPress={() => setRangeOpen(false)}
            style={styles.modalBackdrop}
          />
          <View
            style={[
              styles.actionSheet,
              { backgroundColor: c.surface, borderColor: c.border },
            ]}
          >
            <ThemedText style={styles.rangeTitle}>
              {t(strings.rangeTitle)}
            </ThemedText>
            <ThemedText style={[styles.rangeHint, { color: c.textMuted }]}>
              {t(strings.rangeHint, {
                distance: formatRadiusM(group?.radiusM ?? SMALL_GROUP_RADIUS_MAX_M),
              })}
            </ThemedText>
            <NearbyRadiusSlider
              valueM={group?.radiusM ?? SMALL_GROUP_RADIUS_MAX_M}
              onCommit={commitRadius}
              colors={{
                track: c.tint,
                trackRemaining: c.border,
                thumb: c.tint,
                label: c.text,
                labelMuted: c.textMuted,
              }}
            />
            <Pressable
              onPress={() => setRangeOpen(false)}
              accessibilityRole="button"
              style={[styles.rangeDone, { borderColor: c.border }]}
            >
              <ThemedText style={styles.rangeDoneText}>
                {t(strings.rangeDone)}
              </ThemedText>
            </Pressable>
          </View>
        </View>
      </Modal>

      <Modal
        visible={membersOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setMembersOpen(false)}
      >
        <View style={styles.modalScreen}>
          <BlurredBackdrop
            accessibilityLabel={t(strings.close)}
            style={styles.modalBackdrop}
            onPress={() => setMembersOpen(false)}
          />
          <View
            style={[
              styles.membersSheet,
              { backgroundColor: c.surface, borderColor: c.border },
            ]}
          >
            <ThemedText variant="bodyStrong">{t(strings.membersSheetTitle)}</ThemedText>
            {group?.members.length === 1 ? (
              <ThemedText variant="caption" tone="muted">
                {t(strings.memberOnly)}
              </ThemedText>
            ) : null}

            {/*
              The viewer's own decision, in the one place the roster is.
              
              Here rather than in a settings screen because it is a fact about
              THIS group and nowhere else: consent given in one room is not
              consent given in another, which is why the permission lives on the
              membership row rather than on the account. Putting it beside the
              people it grants access to is the only placement that says so.
              
              Only while the viewer is actually in a running group — an ended
              group's roster is history, and a switch on history is a control
              that does nothing.
            */}
            {viewerMembership && group?.state !== "inactive" ? (
              <Pressable
                accessibilityRole="switch"
                accessibilityState={{
                  checked: viewerMembership.allowMemberCues === true,
                  disabled: savingConsent,
                }}
                accessibilityLabel={t(strings.cueConsentTitle)}
                disabled={savingConsent}
                onPress={() =>
                  void setCueConsent(viewerMembership.allowMemberCues !== true)
                }
                style={[
                  styles.consentRow,
                  { borderColor: c.border, backgroundColor: c.surfaceAlt },
                  savingConsent && styles.consentRowBusy,
                ]}
              >
                <View style={styles.consentCopy}>
                  <ThemedText variant="caption">
                    {t(strings.cueConsentTitle)}
                  </ThemedText>
                  <ThemedText variant="caption" tone="muted">
                    {t(strings.cueConsentHint)}
                  </ThemedText>
                </View>
                <CompactSwitch
                  enabled={viewerMembership.allowMemberCues === true}
                  disabled={savingConsent}
                />
              </Pressable>
            ) : null}
            <ScrollView
              style={styles.membersList}
              contentContainerStyle={styles.membersListContent}
            >
              {group?.members.map((member) => {
                const actions = memberActionsFor({
                  member,
                  isViewer: member.id === currentUserId,
                  viewerIsHost: isHost,
                });
                return (
                  <View
                    key={member.id}
                    style={[styles.memberRow, { borderColor: c.border }]}
                  >
                    <Avatar
                      seed={member.avatarSeed ?? member.id}
                      avatarConfig={member.avatarConfig}
                      name={member.displayName}
                      size={38}
                      accessibilityLabel={member.displayName}
                    />
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={`${t(strings.groupViewProfile)}: ${
                        member.displayName
                      }`}
                      onPress={() => openMemberProfile(member.id)}
                      style={styles.memberCopy}
                    >
                      <View style={styles.memberNameRow}>
                        <ThemedText variant="caption" numberOfLines={1}>
                          {member.displayName}
                        </ThemedText>
                        {member.isHost ? (
                          <ThemedText variant="caption" tone="tint">
                            {t(strings.hostLabel)}
                          </ThemedText>
                        ) : null}
                      </View>
                      {member.ageBand ? (
                        <ThemedText variant="caption" tone="muted">
                          {member.ageBand}
                        </ThemedText>
                      ) : null}
                    </Pressable>
                    <View style={styles.memberActions}>
                      {actions.length === 1 ? (
                        /*
                          One action does not deserve a menu.
                          
                          A "…" that opens to a single Report is two taps and a
                          disappointment: the affordance promises choices and
                          delivers one. So a lone action is drawn as itself —
                          which for most rows, most of the time, is exactly the
                          bare flag icon this screen has always had.
                        */
                        <Pressable
                          accessibilityRole="button"
                          accessibilityLabel={`${memberActionLabel(
                            actions[0],
                          )}: ${member.displayName}`}
                          disabled={cueingMemberId === member.id}
                          onPress={() => runMemberAction(actions[0], member)}
                          style={styles.memberIconButton}
                        >
                          <Ionicons
                            name={MEMBER_ACTION_ICON[actions[0]]}
                            size={18}
                            color={
                              actions[0] === "remove"
                                ? Palette.red
                                : c.textMuted
                            }
                          />
                        </Pressable>
                      ) : actions.length > 1 ? (
                        <Pressable
                          accessibilityRole="button"
                          accessibilityLabel={t(strings.memberActions, {
                            name: member.displayName,
                          })}
                          onPress={() => {
                            // Swap, never stack: iOS will not present a second
                            // native modal window over one already up — the
                            // menu never appears and its window eats every
                            // touch until the screen unmounts (device-observed,
                            // build 25). Same close-then-open the options
                            // sheet uses; cancel swaps back to the roster.
                            setMembersOpen(false);
                            setMenuMemberId(member.id);
                          }}
                          style={styles.memberIconButton}
                        >
                          <Ionicons
                            name="ellipsis-horizontal"
                            size={18}
                            color={c.textMuted}
                          />
                        </Pressable>
                      ) : null}
                    </View>
                  </View>
                );
              })}
            </ScrollView>
          </View>
        </View>
      </Modal>

      <Modal
        visible={meetupOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setMeetupOpen(false)}
      >
        <View style={styles.modalScreen}>
          <BlurredBackdrop
            accessibilityLabel={t(strings.closeSpotsA11y)}
            style={styles.modalBackdrop}
            onPress={() => setMeetupOpen(false)}
          />

          <View
            style={[
              styles.meetupSheet,
              { backgroundColor: c.surface, borderColor: c.border },
            ]}
          >
            <ThemedText variant="bodyStrong">{t(strings.suggestSpotTitle)}</ThemedText>
            <View style={styles.spotGrid}>
              {MEETUP_SPOTS.filter((spot) => spot.value !== "custom").map((spot) => (
                <Pressable
                  key={spot.value}
                  onPress={() => suggestMeetup(spot.value)}
                  style={[styles.spotButton, { borderColor: c.border }]}
                >
                  <ThemedText variant="caption">{t(spot.labelKey)}</ThemedText>
                </Pressable>
              ))}
            </View>

            <ThemedText variant="caption" tone="secondary">
              {t(strings.ownSpot)}
            </ThemedText>
            <View style={styles.customSpotRow}>
              <TextInput
                value={customSpot}
                onChangeText={setCustomSpot}
                placeholder={t(strings.customSpotPlaceholder)}
                placeholderTextColor={c.textMuted}
                returnKeyType="send"
                onSubmitEditing={() => suggestMeetup("custom")}
                style={[
                  styles.customSpotInput,
                  { color: c.text, borderColor: c.border },
                ]}
              />
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={t(strings.deckAddToMessageA11y)}
                disabled={!customSpot.trim()}
                onPress={() => suggestMeetup("custom")}
                style={({ pressed }) => [
                  styles.customSpotSend,
                  { backgroundColor: c.tint },
                  !customSpot.trim() && styles.customSpotSendDisabled,
                  pressed && customSpot.trim() ? styles.pressed : null,
                ]}
              >
                <ThemedText variant="bodyStrong" tone="onTint">
                  {t(strings.deckAddToMessage)}
                </ThemedText>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
      {/*
        The roster action menu.
        
        Rendered only when it has a member, so it holds no stale identity
        between openings — a menu that remembered the last person it was opened
        for is one mis-tap away from reporting or removing the wrong one.
      */}
      <Modal
        visible={menuMember !== undefined}
        transparent
        animationType="fade"
        onRequestClose={() => {
          setMenuMemberId(null);
          setMembersOpen(true);
        }}
      >
        <View style={styles.modalScreen}>
          <BlurredBackdrop
            style={styles.modalBackdrop}
            accessibilityLabel={t(strings.cancel)}
            onPress={() => {
              setMenuMemberId(null);
              setMembersOpen(true);
            }}
          />
          {menuMember ? (
            <View
              style={[
                styles.memberMenu,
                { backgroundColor: c.surface, borderColor: c.border },
              ]}
            >
              <ThemedText variant="bodyStrong" numberOfLines={1}>
                {menuMember.displayName}
              </ThemedText>
              {memberActionsFor({
                member: menuMember,
                isViewer: menuMember.id === currentUserId,
                viewerIsHost: isHost,
              }).map((kind) => (
                <Pressable
                  key={kind}
                  accessibilityRole="button"
                  accessibilityLabel={memberActionLabel(kind)}
                  disabled={kind === "cue" && cueingMemberId === menuMember.id}
                  onPress={() => runMemberAction(kind, menuMember)}
                  style={({ pressed }) => [
                    styles.memberMenuRow,
                    { borderColor: c.border },
                    pressed && styles.memberMenuRowPressed,
                  ]}
                >
                  <Ionicons
                    name={MEMBER_ACTION_ICON[kind]}
                    size={18}
                    color={kind === "remove" ? Palette.red : c.textMuted}
                  />
                  <ThemedText
                    variant="caption"
                    tone={kind === "remove" ? "danger" : undefined}
                  >
                    {memberActionLabel(kind)}
                  </ThemedText>
                </Pressable>
              ))}
            </View>
          ) : null}
        </View>
      </Modal>
      <ReportModal
        visible={reportMemberId !== null}
        reportedUserId={reportMemberId ?? ""}
        onClose={() => setReportMemberId(null)}
        onSubmitted={() => setReportMemberId(null)}
      />
    </SafeAreaView>
  );
}

function GroupTypingIndicator({
  participants,
  colors,
}: {
  participants: TypingParticipant[];
  colors: ReturnType<typeof useTheme>;
}) {
  const { t } = useTranslation();
  if (participants.length === 0) return null;

  const label =
    participants.length === 1
      ? t(strings.oneTyping, { name: participants[0].displayName })
      : t(strings.manyTyping, { count: participants.length });

  return (
    <View
      accessibilityRole="text"
      accessibilityLabel={label}
      style={styles.typingRow}
    >
      <View style={styles.typingAvatarStack}>
        {participants.map((participant, index) => (
          <View
            key={participant.userId}
            style={[
              styles.typingAvatarWrap,
              {
                marginLeft: index === 0 ? 0 : -8,
                borderColor: colors.background,
              },
            ]}
          >
            <Avatar
              seed={participant.userId}
              name={participant.displayName}
              size={24}
            />
          </View>
        ))}
      </View>

      <View
        style={[
          styles.typingBubble,
          {
            backgroundColor: colors.backgroundSelected,
            borderColor: colors.border,
          },
        ]}
      >
        {[0, 1, 2].map((dot) => (
          <View
            key={dot}
            style={[styles.typingDot, { backgroundColor: colors.textMuted }]}
          />
        ))}
      </View>
    </View>
  );
}

function GroupMessageBubble({
  message,
  mine,
}: {
  message: SmallGroupMessage;
  mine: boolean;
}) {
  const c = useTheme();
  const { t } = useTranslation();
  const time = formatMessageTime(message.timestamp);
  if (message.kind === "system") {
    return (
      <View style={styles.systemMessage}>
        <ThemedText variant="caption" tone="muted" center>
          {systemMessageText(message)}
        </ThemedText>
      </View>
    );
  }

  return (
    <View style={[styles.messageRow, mine ? styles.messageRight : styles.messageLeft]}>
      <View style={[styles.messageStack, mine && styles.messageStackRight]}>
        <View
          style={[
            styles.bubble,
            {
              backgroundColor: mine ? c.tint : c.surface,
              borderColor: c.border,
              borderWidth: mine ? 0 : 1,
              borderBottomRightRadius: mine ? 4 : Radius.lg,
              borderBottomLeftRadius: mine ? Radius.lg : 4,
            },
          ]}
        >
          {!mine ? (
            <ThemedText variant="caption" tone="muted">
              {message.senderName ?? "Someone"}
            </ThemedText>
          ) : null}
          <ThemedText
            variant="body"
            style={[styles.messageText, { color: mine ? c.onTint : c.text }]}
          >
            {message.content}
          </ThemedText>
          {time ? (
            // Group messages carry a send time now, and this is where the
            // build-13 shake goes to die.
            //
            // The previous version had no timestamp, so the outbox's clock had
            // nothing to replace: it was an extra row that appeared on send and
            // vanished on delivery, and every appearance and disappearance
            // resized the bubble. In an inverted thread that moves every
            // message above it. Reserving blank space to hold a rare marker
            // would have been padding every bubble in the app to serve the
            // uncommon case; a timestamp is something the thread wanted anyway
            // and it holds the same slot open for free.
            //
            // Same construction as `chat/[id].tsx`: fixed height, the text
            // always rendered so it decides the geometry once, the marker laid
            // over it.
            <View style={styles.messageTimeSlot}>
              <ThemedText
                variant="caption"
                style={[
                  styles.messageTime,
                  { color: mine ? c.onTint : c.textMuted },
                  message.pending && styles.messageTimeReserved,
                ]}
                accessibilityElementsHidden={message.pending}
                importantForAccessibility={
                  message.pending ? "no-hide-descendants" : "auto"
                }
              >
                {time}
              </ThemedText>

              {message.pending && message.pendingSlow ? (
                <Ionicons
                  name="time-outline"
                  size={12}
                  color={mine ? c.onTint : c.textMuted}
                  style={styles.messageTimeMarker}
                  accessibilityLabel={t(strings.messagePending)}
                />
              ) : null}
            </View>
          ) : null}
        </View>
      </View>
    </View>
  );
}

function GroupAction({
  icon,
  label,
  onPress,
  destructive = false,
}: {
  icon: GroupActionIcon;
  label: string;
  onPress: () => void;
  destructive?: boolean;
}) {
  const c = useTheme();
  const color = destructive ? c.danger : c.text;

  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      style={({ pressed }) => [
        styles.sheetAction,
        { backgroundColor: pressed ? c.backgroundSelected : "transparent" },
      ]}
    >
      <Ionicons name={icon} size={20} color={color} />
      <ThemedText variant="bodyStrong" style={{ color }}>
        {label}
      </ThemedText>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1 },
  flex: { flex: 1 },
  disabled: { opacity: 0.45 },
  header: {
    width: "100%",
    maxWidth: CHAT_MAX_WIDTH,
    alignSelf: "center",
    minHeight: 62,
    paddingHorizontal: Spacing.lg,
    paddingBottom: Spacing.md,
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.sm,
    borderBottomWidth: 1,
  },
  headerWeb: {
    maxWidth: "100%",
    alignSelf: "stretch",
    paddingHorizontal: 24,
    paddingTop: 12,
    paddingBottom: 12,
  },
  back: { marginRight: Spacing.xs / 2 },
  headerCopy: { flex: 1, minWidth: 0 },
  optionsButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
  },
  goalBand: {
    width: "100%",
    maxWidth: CHAT_MAX_WIDTH,
    alignSelf: "center",
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.sm,
    borderBottomWidth: 1,
  },
  expiryBanner: {
    width: "100%",
    maxWidth: CHAT_MAX_WIDTH,
    alignSelf: "center",
    minHeight: 38,
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.sm,
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.xs,
    borderBottomWidth: 1,
  },
  loadingOlder: {
    paddingVertical: Spacing.sm,
  },
  messages: {
    width: "100%",
    maxWidth: CHAT_MAX_WIDTH,
    alignSelf: "center",
    flexGrow: 1,
    gap: Spacing.sm,
    padding: Spacing.lg,
  },
  empty: { flex: 1, justifyContent: "center", paddingVertical: Spacing.xxxl },
  typingRow: {
    width: "100%",
    maxWidth: CHAT_MAX_WIDTH,
    alignSelf: "center",
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: Spacing.lg,
    paddingBottom: Spacing.xs,
  },
  typingAvatarStack: {
    flexDirection: "row",
    alignItems: "center",
  },
  typingAvatarWrap: {
    borderWidth: 2,
    borderRadius: 14,
  },
  typingBubble: {
    minWidth: 42,
    height: 26,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    borderRadius: 13,
    borderWidth: 1,
  },
  typingDot: {
    width: 5,
    height: 5,
    borderRadius: 3,
    opacity: 0.72,
  },
  messageRow: { flexDirection: "row", position: "relative" },
  messageLeft: { justifyContent: "flex-start" },
  messageRight: { justifyContent: "flex-end" },
  messageStack: {
    maxWidth: "78%",
    minWidth: 0,
    alignItems: "flex-start",
    gap: 4,
  },
  messageStackRight: {
    alignItems: "flex-end",
  },
  bubble: {
    maxWidth: "100%",
    minWidth: 0,
    gap: 3,
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.md,
    borderRadius: Radius.lg,
  },
  messageText: {
    minWidth: 0,
    flexShrink: 1,
  },
  /**
   * `minHeight`, NOT `height` — see the 1:1 thread's copy for why a hard
   * height would clip the caption at large OS font settings, and why the
   * invariant does not need one.
   */
  messageTimeSlot: {
    alignSelf: "flex-end",
    minHeight: 15,
    justifyContent: "center",
  },
  messageTime: {
    fontSize: 11,
    lineHeight: 15,
  },
  /** Present, measured, and not shown. */
  messageTimeReserved: {
    opacity: 0,
  },
  messageTimeMarker: {
    // Laid OVER the reserved timestamp rather than beside it, so raising the
    // marker cannot change the slot's width any more than its height.
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    fontSize: 11,
    textAlign: "center",
    textAlignVertical: "center",
  },
  systemMessage: {
    alignSelf: "center",
    maxWidth: "88%",
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.xs,
  },
  inputBar: {
    width: "100%",
    maxWidth: CHAT_MAX_WIDTH,
    alignSelf: "center",
    flexDirection: "row",
    alignItems: "flex-end",
    gap: Spacing.sm,
    paddingHorizontal: Spacing.lg,
    paddingTop: Spacing.md,
    paddingBottom: Spacing.md,
  },
  inputBarWeb: {
    paddingTop: Spacing.sm,
  },
  input: {
    flex: 1,
    maxHeight: 120,
    minHeight: 44,
    borderRadius: Radius.lg,
    borderWidth: 1.5,
    paddingHorizontal: Spacing.lg,
    paddingTop: Platform.OS === "ios" ? 12 : 8,
    paddingBottom: Platform.OS === "ios" ? 12 : 8,
    fontSize: 16,
  },
  sendBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: "center",
    justifyContent: "center",
  },
  modalScreen: {
    flex: 1,
    justifyContent: "flex-end",
  },
  modalBackdrop: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: "rgba(15, 23, 42, 0.42)",
  },
  actionSheet: {
    margin: Spacing.lg,
    padding: Spacing.sm,
    gap: 2,
    borderRadius: Radius.lg,
    borderWidth: 1,
  },
  sheetAction: {
    minHeight: 48,
    paddingHorizontal: Spacing.md,
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.md,
    borderRadius: Radius.md,
  },
  rangeTitle: { fontSize: 16, fontWeight: "800", marginBottom: 4 },
  rangeHint: {
    fontSize: 13,
    lineHeight: 18,
    marginBottom: Spacing.sm,
  },
  rangeDone: {
    borderWidth: 1,
    borderRadius: Radius.md,
    paddingVertical: 12,
    alignItems: "center",
    marginTop: Spacing.md,
  },
  rangeDoneText: { fontSize: 14, fontWeight: "700" },
  sheetDivider: {
    height: 1,
    marginVertical: Spacing.xs,
  },
  membersSheet: {
    margin: Spacing.lg,
    padding: Spacing.lg,
    gap: Spacing.sm,
    borderRadius: Radius.lg,
    borderWidth: 1,
    maxHeight: "82%",
  },
  membersList: {
    maxHeight: 360,
  },
  membersListContent: {
    paddingBottom: Spacing.xs,
  },
  memberRow: {
    minHeight: 56,
    paddingVertical: Spacing.sm,
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.sm,
    borderBottomWidth: 1,
  },
  memberCopy: {
    flex: 1,
    minWidth: 0,
  },
  memberNameRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.xs,
  },
  memberActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.xs,
  },
  memberIconButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: "center",
    justifyContent: "center",
  },
  /**
   * The roster action menu. Narrower than the members sheet it opens from, so
   * it reads as sitting on top of that list rather than replacing it.
   */
  memberMenu: {
    margin: Spacing.lg,
    padding: Spacing.lg,
    gap: Spacing.xs,
    borderRadius: Radius.lg,
    borderWidth: 1,
    alignSelf: "center",
    minWidth: 240,
    maxWidth: 340,
  },
  memberMenuRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.sm,
    paddingVertical: 12,
    borderTopWidth: 1,
  },
  memberMenuRowPressed: { opacity: 0.6 },
  /**
   * The viewer's own cue consent, above the roster.
   *
   * A row rather than a bare switch: the copy under the title is load-bearing —
   * it says that turning this off does not withdraw Cues already sent, which is
   * the one thing about it that is not obvious and the one thing somebody would
   * otherwise assume the opposite of.
   */
  consentRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.sm,
    padding: Spacing.md,
    borderRadius: Radius.md,
    borderWidth: 1,
  },
  consentRowBusy: { opacity: 0.6 },
  consentCopy: { flex: 1, gap: 2 },
  removedCard: {
    width: "100%",
    maxWidth: CHAT_MAX_WIDTH,
    alignSelf: "center",
    marginBottom: Spacing.sm,
    padding: Spacing.lg,
    gap: Spacing.sm,
    borderRadius: Radius.lg,
    borderWidth: 1,
  },
  removedButton: {
    minHeight: 40,
    paddingHorizontal: Spacing.lg,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: Radius.lg,
  },
  meetupSheet: {
    margin: Spacing.lg,
    padding: Spacing.lg,
    gap: Spacing.md,
    borderRadius: Radius.lg,
    borderWidth: 1,
  },
  spotGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: Spacing.sm,
  },
  spotButton: {
    minHeight: 38,
    paddingHorizontal: Spacing.md,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: Radius.lg,
    borderWidth: 1,
  },
  customSpotRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.sm,
  },
  customSpotInput: {
    flex: 1,
    minHeight: 44,
    paddingHorizontal: Spacing.md,
    borderRadius: Radius.lg,
    borderWidth: 1,
    fontSize: 15,
  },
  customSpotSend: {
    minHeight: 44,
    paddingHorizontal: Spacing.lg,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: Radius.lg,
  },
  customSpotSendDisabled: {
    opacity: 0.45,
  },
  pressed: {
    opacity: 0.82,
  },
});
